import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import {TranslateModule} from "@ngx-translate/core";
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {EditTranslationComponent} from "./EditTranslation/edit-translation.component";
import {Subscription} from "rxjs";
import {FilterPaginationComponent} from "../../../common/tables/FilterPagination/filter-pagination.component";
import ISubSystemItem = NCVIB.app.Main.Menu.Structure.ISubSystemItem;
import ITranslationViewData = NCVIB.app.Main.Administration.Translation.ITranslationViewData;
import ILanguageViewData = NCVIB.app.Main.Administration.Translation.ILanguageViewData;
import {IExpandable, IPaginationSettings, PagedDataService} from "../../../common/tables/PagedDataService";
import {UserRolePermissionsService} from "../../../common/user-role-permissions.service";
import {TitleService} from "../../../common/title.service";
import {CoreService} from "../../../common/core.service";
import {CommonModule} from "@angular/common";
import {CdkTable} from "@angular/cdk/table";
import {MatSortModule} from "@angular/material/sort";
import {MatTableModule} from "@angular/material/table";
import Permission = NCVIB.Domain.Entities.Permission;
import {DialogService, Translate} from "../../../common/dialogs/dialog.service";
import IDeleteTranslationCommand = NCVIB.app.Main.Administration.Translation.DeleteTranslation.IDeleteTranslationCommand;
import {TrackingTranslateService} from "../../../common/translation/tracking-translate.service";
import {TranslationTrackingService} from "../../../common/translation/translation-tracking.service";
import {TrackTranslatePipe} from "../../../common/translation/track-translate.pipe";

@Component({
    selector: 'translation',
    templateUrl: './translation.component.html',
    styleUrls: ['./translation.component.css'],
    providers: [PagedDataService],
    imports: [
        CommonModule,
        CdkTable,
        MatSortModule,
        MatTableModule,
        FilterPaginationComponent,
        NgbDropdownMenu,
        NgbDropdownToggle,
        NgbDropdown,
        TrackTranslatePipe
    ]
})
export class TranslationComponent implements AfterViewInit, OnDestroy, IPaginationSettings {
    constructor(
        public userRolePermissionsService: UserRolePermissionsService,
        private titleService: TitleService,
        private core: CoreService,
        private modalService: NgbModal,
        private dialogService: DialogService,
        public cd: ChangeDetectorRef,
        public pagedDataService: PagedDataService<ITranslationViewData>,
        protected translationTrackingService: TranslationTrackingService,
        protected translationService: TrackingTranslateService) {
        this.titleService.setTitle('Translation');
        this.checkMayDeleteUnusedTranslations().then(() =>
            this.loadLanguages().then(() =>
                this.loadTranslations(this.currentReferenceLanguage.languageCode, this.currentTranslationLanguage.languageCode).then()));

        this.isReadOnly = (this.userRolePermissionsService.subSystemStructure!.administration.translation.permission & Permission.Write) == 0;
    }

    public isReadOnly: boolean = true;

    @ViewChild(FilterPaginationComponent) pagination: FilterPaginationComponent | undefined;
    @ViewChild('translationsTable', {read: ViewContainerRef, static: false}) tableElement: ViewContainerRef | undefined;
    @ViewChild('translationsTableHeaderRow', {read: ViewContainerRef, static: false}) tableHeaderRowElement: ViewContainerRef | undefined;
    @ViewChildren('translationsTableRow', {read: ViewContainerRef}) tableRowElements: QueryList<ViewContainerRef> | undefined;

    public defaultSortColumn: string = "";
    public sortAscending: boolean = false;
    public subSystem: ISubSystemItem = this.userRolePermissionsService.subSystemStructure!.administration.translation;

    public get columnDefinitions(): any[] {
        return ['edit', 'inUse', 'key', 'referenceText', 'translationText', 'sourceUrl', 'delete'];
    };

    public mayDeleteUnusedTranslations: boolean = false;
    public translations: ITranslationViewData[] = [];
    public languages: ILanguageViewData[] = [];
    public currentReferenceLanguage: ILanguageViewData = {languageCode: "en-GB", flagClass: "en", label: "English", isSelected: false};
    public currentTranslationLanguage: ILanguageViewData = {languageCode: "sv", flagClass: "sv", label: "Swedish", isSelected: true};
    private rowChangesSubscription: Subscription | undefined;
    protected filterOnUntranslatedKeys: boolean = true;

    public ngAfterViewInit(): void {
        this.rowChangesSubscription = this.tableRowElements!.changes.subscribe((comps: QueryList<ViewContainerRef>) => {
            this.pagedDataService.tableRowElement = comps.first;
        });
        this.tableRowElements?.notifyOnChanges();
    }

    public ngOnDestroy() {
        this.rowChangesSubscription?.unsubscribe();
        this.cd.detach();
    }

    public typed(value: any): ITranslationViewData {
        return value;
    }

    public loadLanguages(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.core.get<ILanguageViewData[]>(`api/v2/administration/get-languages`,
                "Load languages",
                false)
                .subscribe((viewData: any) => {
                    this.languages = viewData;
                    this.currentTranslationLanguage = this.languages.filter(x => x.isSelected)[0];
                    this.detectChanges();
                    resolve();
                });
        });
    }

    public loadTranslations(referenceLanguage: string, translationLanguage: string): Promise<void> {
        return new Promise<void>((resolve) => {
            this.core.get<ITranslationViewData[]>(`api/v2/administration/get-translations/${referenceLanguage}/${translationLanguage}`,
                "Load translations",
                false)
                .subscribe((viewData: ITranslationViewData[]) => {
                    this.translations = viewData;
                    this.pagedDataService.initialize(this as IPaginationSettings, this.translations);
                    this.updatePagedServiceTranslations();
                    resolve();
                });
        });
    }

    public selectReferenceLanguage(language: ILanguageViewData) {
        this.currentReferenceLanguage = language;
        this.loadTranslations(this.currentReferenceLanguage.languageCode, this.currentTranslationLanguage.languageCode).then();
    }

    public selectTranslationLanguage(language: ILanguageViewData) {
        this.currentTranslationLanguage = language;
        this.loadTranslations(this.currentReferenceLanguage.languageCode, this.currentTranslationLanguage.languageCode).then();
    }

    public numberOfUntranslatedReferenceTexts(): number {
        return this.translations.filter(x => !x.isReferenceTranslated).length;
    }

    public numberOfUntranslatedTranslationTexts(): number {
        return this.translations.filter(x => !x.isTranslated).length;
    }

    public deleteUnusedTranslations() {
        this.core.get<void>(`api/v2/administration/smoke-tests/delete-unused-translations`, "Delete unusedTranslations", true)
            .subscribe();
    }

    public editTranslation(translation: ITranslationViewData) {
        let size = this.calculateSizeOfFormFromText(translation);
        const translationModal = this.modalService.open(EditTranslationComponent, {size: size, backdrop: 'static'});
        const component: EditTranslationComponent = translationModal.componentInstance;
        component.setLanguages(this.currentReferenceLanguage, this.currentTranslationLanguage);
        component.setTranslation(translation);

        translationModal.result
            .then(
                command => {
                    if (translation.referenceText != command.referenceText) {
                        translation.isReferenceTranslated = true;
                        translation.referenceText = command.referenceText;
                    }

                    if (translation.translationText != command.newTranslation) {
                        translation.isTranslated = true;
                        translation.translationText = command.newTranslation;
                    }

                    this.translationService.reloadLang(this.currentTranslationLanguage.languageCode);
                    this.detectChanges();
                },
                () => {
                });

    }

    public deleteTranslation(translation: ITranslationViewData) {
        const confirmed = this.dialogService.openConfirmDialog(
            this.translationService.instant('DeleteConfirmation'),
            `${this.translationService.instant('AreYouSureYouWantToDeleteTranslation')}: ${translation.key}`,
            Translate.No);
        confirmed.then(
            () => {
                const command: IDeleteTranslationCommand = {
                    id: translation!.id,
                };

                this.core.post<void>(`api/v2/administration/translation/delete-translation`,
                    command,
                    "Delete Translation",
                    true)
                    .subscribe(x => {
                        this.reload();
                    });
            }, () => {
                console.log("cancelled");
            }).catch((e: any) => {
            console.error(e);
        });
    }

    public reload() {
        this.loadTranslations(this.currentReferenceLanguage.languageCode, this.currentTranslationLanguage.languageCode).then();
    }

    public checkMayDeleteUnusedTranslations() {
        return new Promise<void>((resolve) => {
            this.core.get<boolean>(`api/v2/administration/may-delete-unused-translations`,
                "Check if user may delete unused translations",
                false)
                .subscribe((result: any) => {
                    this.mayDeleteUnusedTranslations = result;
                    resolve();
                    this.detectChanges();
                });
        });
    }

    private calculateSizeOfFormFromText(translation: ITranslationViewData): string {
        let maxLength = Math.max(translation.key.length, translation.referenceText?.length ?? 0, translation.translationText?.length ?? 0);

        if (maxLength < 50) {
            return "";
        } else if (maxLength < 100) {
            return "lg";
        }

        return "xl";
    }

    public isUrl(sourceUrl: string | undefined): boolean {
        return sourceUrl?.startsWith("/") ?? false;
    }

    public toUrl(sourceUrl: string | undefined): string {
        let parts = sourceUrl?.split(" ") ?? [];
        return this.isUrl(sourceUrl) ? window.location.origin + parts[0] : "/";
    }

    private detectChanges() {
        try {
            this.cd.detectChanges();
        } catch (e) {
        }
    }

    protected updatePagedServiceTranslations() {
        const untranslatedKeys = this.translationTrackingService.untranslatedKeys;
        if (this.filterOnUntranslatedKeys && untranslatedKeys.size > 0) {
            this.pagedDataService.updateData(this.translations.filter(x => untranslatedKeys.has(x.key)));
        } else {
            this.pagedDataService.updateData(this.translations);
        }
        console.log(this.translations, untranslatedKeys);
        this.detectChanges();
    }
}
